import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Button,
  Chip,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import { orderService } from "../../services/orderService";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { ManualBankTransferInstructions } from "../../components/checkout/PaymentStep";
import { getStatusChipColor } from "../../utils/statusUtils";

// Helper function to copy text (Consider moving to utils)
const copyToClipboard = (text, feedbackMessage) => {
  navigator.clipboard.writeText(text).then(
    () => {
      toast.success(feedbackMessage || "Copied to clipboard!");
    },
    (err) => {
      toast.error("Failed to copy!");
      console.error("Could not copy text: ", err);
    }
  );
};

// Helper to check if order is in a final, uneditable state
const isOrderInFinalState = (orderStatus) => {
  return [
    "canceled",
    "failed",
    "delivered",
    "returned",
    "return_rejected",
  ].includes(orderStatus);
};

const AdminOrderDetailPage = () => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("orders");

  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // State for editable fields
  const [editableNotes, setEditableNotes] = useState("");
  const [notesChanged, setNotesChanged] = useState(false);

  // State for confirmation dialogs
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState({
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [trackingNumber, setTrackingNumber] = useState("");

  // Fetch order details
  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setError(tCommon("status.notFound"));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setActionLoading(null);
    try {
      const data = await orderService.getOrderById(orderId, true);
      setOrder(data);
      setEditableNotes(data.notes || "");
      setNotesChanged(false);
    } catch (err) {
      setError(err.message || tCommon("status.error"));
      console.error(`Fetch admin order ${orderId} error:`, err);
    } finally {
      setLoading(false);
    }
  }, [orderId, tCommon]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  useEffect(() => {
    document.title = t("admin.detail.title", {
      orderNumber: order?.orderNumber || orderId,
    });
    return () => {
      document.title = "B2B E-Commerce";
    };
  }, [order, orderId, t]);

  useEffect(() => {
    setNotesChanged(editableNotes !== (order?.notes || ""));
  }, [editableNotes, order?.notes]);

  // Handle saving changes
  const handleSaveChanges = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const updateData = {};
      if (editableNotes !== (order.notes || ""))
        updateData.notes = editableNotes;

      if (Object.keys(updateData).length > 0) {
        const updatedOrder = await orderService.updateOrderNotes(
          orderId,
          updateData.notes
        );
        setOrder(updatedOrder);
        setEditableNotes(updatedOrder.notes || "");
        setNotesChanged(false);
        toast.success(t("admin.detail.success.notesUpdated"));
      } else {
        toast.info(tCommon("status.success"));
      }
    } catch (err) {
      setError(err.message || tCommon("status.error"));
      toast.error(tCommon("status.error"));
      console.error(`Update admin order ${orderId} error:`, err);
    } finally {
      setIsSaving(false);
    }
  };

  // Action Handlers
  const handleAction = async (
    actionName,
    serviceCall,
    successMessage,
    requiresDialog = false,
    dialogData = {}
  ) => {
    if (requiresDialog) {
      setDialogContent({
        ...dialogData,
        onConfirm: () => executeAction(actionName, serviceCall, successMessage),
      });
      setOpenConfirmDialog(true);
      return;
    }
    executeAction(actionName, serviceCall, successMessage);
  };

  const executeAction = async (actionName, serviceCall, successMessage) => {
    console.log(`Executing action: ${actionName} for order ${orderId}`);
    setActionLoading(actionName);
    setError(null);
    setOpenConfirmDialog(false);

    try {
      // For shipping orders, the serviceCall function will already log the tracking number
      // We don't need to track it separately anymore
      const updatedOrder = await serviceCall();
      console.log(`Action ${actionName} successful. Response:`, updatedOrder);
      setOrder(updatedOrder);
      setEditableNotes(updatedOrder.notes || "");
      setNotesChanged(false);
      toast.success(successMessage || tCommon("status.success"));
    } catch (err) {
      const errMsg =
        err.response?.data?.message || err.message || tCommon("status.error");
      console.error(`Action ${actionName} failed. Error:`, err);
      setError(errMsg);
      toast.error(errMsg);
      console.error(
        `Admin action ${actionName} for order ${orderId} error:`,
        err
      );
    } finally {
      setActionLoading(null);
      setTrackingNumber("");
    }
  };

  const handleConfirmPayment = () => {
    handleAction(
      "confirmPayment",
      () => orderService.confirmManualPayment(orderId),
      t("admin.detail.success.paymentConfirmed"),
      true,
      {
        title: t("admin.detail.dialog.confirmPayment.title"),
        message: t("admin.detail.dialog.confirmPayment.message", {
          orderNumber: order.orderNumber,
        }),
      }
    );
  };

  const handleDownloadInvoice = async (variant = null) => {
    setActionLoading("downloadInvoice");
    try {
      const blob = await orderService.downloadInvoice(orderId, true, variant);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const variantSuffix = variant ? `_${variant}` : "";
      link.download = `invoice_${order.orderNumber}${variantSuffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t("admin.detail.success.invoiceDownloaded", "Invoice downloaded successfully"));
    } catch (err) {
      const errMsg =
        err.response?.data?.message || err.message || tCommon("status.error");
      toast.error(errMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkAsShipped = () => {
    setDialogContent({
      title: t("admin.detail.dialog.markAsShipped.title"),
      message: t("admin.detail.dialog.markAsShipped.message", {
        orderNumber: order.orderNumber,
      }),
      onConfirm: (shipFn) => {
        // The shipFn will be provided when the dialog's confirm button is clicked
        // It will contain the current tracking number value
        executeAction(
          "markAsShipped",
          shipFn, // This function will be provided when confirm is clicked
          t("admin.detail.success.shipped")
        );
      },
    });
    setOpenConfirmDialog(true);
  };

  const handleCancelOrder = () => {
    handleAction(
      "cancelOrder",
      () => orderService.cancelOrderAdmin(orderId),
      t("admin.detail.success.canceled"),
      true,
      {
        title: t("admin.detail.dialog.cancelOrder.title"),
        message: t("admin.detail.dialog.cancelOrder.message", {
          orderNumber: order.orderNumber,
        }),
      }
    );
  };

  if (loading) {
    return (
      <Container
        maxWidth="md"
        sx={{ mt: 4, mb: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!loading && error && !actionLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
        <Button component={RouterLink} to="/admin/orders">
          {t("admin.detail.backToOrders")}
        </Button>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography>{tCommon("status.notFound")}</Typography>
        <Button component={RouterLink} to="/admin/orders">
          {t("admin.detail.backToOrders")}
        </Button>
      </Container>
    );
  }

  // Determine if the order notes section should be disabled
  const isLocked = isOrderInFinalState(order.status);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {t("admin.detail.title", { orderNumber: order.orderNumber })}
        </Typography>
        <Button component={RouterLink} to="/admin/orders" variant="outlined">
          {t("admin.detail.backToOrders")}
        </Button>
      </Box>

      {error && actionLoading && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" gutterBottom>
              {t("admin.detail.orderStatus")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Chip
                label={t(
                  `shared.status.${order.status?.toLowerCase()}`,
                  order.status || t("shared.status.unknown")
                )}
                color={getStatusChipColor(order.status)}
              />
              <Chip
                label={t(
                  `shared.status.${order.paymentStatus?.toLowerCase()}`,
                  order.paymentStatus || t("shared.status.unknown")
                )}
                color={getStatusChipColor(order.paymentStatus)}
                variant="outlined"
              />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t("user.detail.actions.title")}
            </Typography>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}
            >
              {/* Download Invoice Button - Always shows auto-detect */}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadInvoice()}
                disabled={actionLoading === "downloadInvoice"}
                fullWidth
              >
                {actionLoading === "downloadInvoice" ? (
                  <CircularProgress size={24} />
                ) : (
                  t("admin.detail.actions.downloadInvoice", "Download Invoice")
                )}
              </Button>

              <Divider sx={{ my: 1 }} />

              {order.status === "awaiting_payment" &&
                order.paymentStatus === "pending" && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleConfirmPayment}
                    disabled={actionLoading === "confirmPayment"}
                  >
                    {actionLoading === "confirmPayment" ? (
                      <CircularProgress size={24} />
                    ) : (
                      t("admin.detail.actions.confirmPayment")
                    )}
                  </Button>
                )}

              {order.status === "processing" &&
                order.paymentStatus === "paid" && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<LocalShippingIcon />}
                    onClick={handleMarkAsShipped}
                    disabled={actionLoading === "markAsShipped"}
                  >
                    {actionLoading === "markAsShipped" ? (
                      <CircularProgress size={24} />
                    ) : (
                      t("admin.detail.actions.markAsShipped")
                    )}
                  </Button>
                )}

              {["pending", "awaiting_payment", "processing"].includes(
                order.status
              ) && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelOrder}
                  disabled={actionLoading === "cancelOrder"}
                >
                  {actionLoading === "cancelOrder" ? (
                    <CircularProgress size={24} />
                  ) : (
                    t("admin.detail.actions.cancelOrder")
                  )}
                </Button>
              )}

              {!isLocked &&
                order.status !== "awaiting_payment" &&
                order.status !== "processing" &&
                !["pending", "awaiting_payment", "processing"].includes(
                  order.status
                ) && (
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.detail.actions.noActions")}
                  </Typography>
                )}
              {isLocked && (
                <Typography variant="body2" color="text.secondary">
                  {t("admin.detail.actions.orderFinalState")}
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ mt: "auto" }}>
              {t("admin.detail.internalNotes")}
            </Typography>
            <TextField
              label={t("admin.detail.internalNotes")}
              multiline
              rows={4}
              fullWidth
              margin="normal"
              value={editableNotes}
              onChange={(e) => setEditableNotes(e.target.value)}
              variant="outlined"
              disabled={isLocked || actionLoading === "saveNotes"}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleSaveChanges}
              disabled={
                isLocked || !notesChanged || actionLoading === "saveNotes"
              }
              sx={{ mt: 1, alignSelf: "flex-end" }}
            >
              {actionLoading === "saveNotes" ? (
                <CircularProgress size={20} />
              ) : (
                t("admin.detail.saveNotes")
              )}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              {t("user.detail.orderSummary")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={t("shared.orderDetails.orderNumber")}
                  secondary={order.orderNumber}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("shared.orderDetails.orderDate")}
                  secondary={format(new Date(order.createdAt), "PPpp")}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("shared.orderDetails.paymentMethod")}
                  secondary={order.paymentMethod || "N/A"}
                />
              </ListItem>
              {order.amountPaidByWallet > 0 && (
                <ListItem>
                  <ListItemText
                    primary={t("shared.orderDetails.walletPayment")}
                    secondary={`${parseFloat(order.amountPaidByWallet).toFixed(
                      2
                    )} CHF`}
                  />
                </ListItem>
              )}
              {order.amountPaidByWallet > 0 &&
                order.paymentMethod !== "wallet" && (
                  <ListItem>
                    <ListItemText
                      primary={
                        order.paymentMethod === "credit_card"
                          ? t("shared.orderDetails.cardPayment")
                          : order.paymentMethod === "bank_transfer_manual"
                          ? t("shared.orderDetails.bankTransferPayment")
                          : order.paymentMethod
                      }
                      secondary={`${(
                        parseFloat(order.total) -
                        parseFloat(order.amountPaidByWallet)
                      ).toFixed(2)} CHF`}
                    />
                  </ListItem>
                )}
              <ListItem>
                <ListItemText
                  primary={t("shared.orderDetails.totalAmount")}
                  secondary={`${
                    typeof order.total === "number" ||
                    (typeof order.total === "string" &&
                      !isNaN(parseFloat(order.total)))
                      ? parseFloat(order.total).toFixed(2)
                      : "N/A"
                  } CHF`}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              {t("admin.detail.customer.title")}
            </Typography>
            {order.user ? (
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={t("admin.detail.customer.name")}
                    secondary={order.user.companyName || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t("admin.detail.customer.company")}
                    secondary={order.user.companyName || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t("admin.detail.customer.email")}
                    secondary={order.user.email}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t("admin.detail.customer.phone")}
                    secondary={order.user.phoneNumber || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <Button
                    size="small"
                    component={RouterLink}
                    to={`/admin/users/${order.user.id}`}
                    sx={{ mt: 1 }}
                  >
                    {t("admin.detail.customer.viewUser")}
                  </Button>
                </ListItem>
              </List>
            ) : (
              <Typography>{t("admin.detail.customer.notAvailable")}</Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" gutterBottom>
                  {t("shared.address.shipping")}
                </Typography>
                <Typography variant="body2">{order.shippingAddress}</Typography>
                <Typography variant="body2">
                  {order.shippingPostalCode} {order.shippingCity}
                </Typography>
                <Typography variant="body2">{order.shippingCountry}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" gutterBottom>
                  {t("shared.address.billing")}
                </Typography>
                <Typography variant="body2">{order.billingAddress}</Typography>
                <Typography variant="body2">
                  {order.billingPostalCode} {order.billingCity}
                </Typography>
                <Typography variant="body2">{order.billingCountry}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {order.paymentMethod === "bank_transfer_manual" &&
          order.paymentInstructions && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{ p: 2, backgroundColor: "grey.50" }}
              >
                <Typography variant="h6" gutterBottom>
                  {t("admin.detail.bankTransferInstructions.title")}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t("admin.detail.bankTransferInstructions.description")}
                </Typography>
                <ManualBankTransferInstructions
                  instructions={order.paymentInstructions}
                />
              </Paper>
            </Grid>
          )}

        {/* Add Shipping Method Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("admin.detail.shippingMethod", "Shipping Information")}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body1">
                <strong>
                  {t("admin.detail.shippingMethodName", "Shipping Method")}:
                </strong>{" "}
                {order.shippingMethod?.name ||
                  order.shippingMethodName ||
                  "Standard Shipping"}
              </Typography>

              {order.shippingMethod?.description && (
                <Typography variant="body2">
                  {order.shippingMethod.description}
                </Typography>
              )}

              <Typography variant="body1">
                <strong>
                  {t("admin.detail.estimatedDelivery", "Estimated Delivery")}:
                </strong>{" "}
                {order.shippingMethod?.estimatedDeliveryTime ||
                  order.estimatedDeliveryTime ||
                  "Not specified"}
              </Typography>

              <Typography variant="body1">
                <strong>
                  {t("admin.detail.shippingCost", "Shipping Cost")}:
                </strong>{" "}
                {parseFloat(order.shipping).toFixed(2)} CHF
              </Typography>

              {order.status === "shipped" && order.metadata?.trackingNumber && (
                <>
                  <Typography
                    variant="body1"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <strong>
                      {t("admin.detail.trackingNumber", "Tracking Number")}:
                    </strong>{" "}
                    <span>{order.metadata.trackingNumber}</span>
                    <IconButton
                      size="small"
                      onClick={() =>
                        copyToClipboard(
                          order.metadata.trackingNumber,
                          t(
                            "admin.detail.trackingCopied",
                            "Tracking number copied to clipboard"
                          )
                        )
                      }
                      aria-label="Copy tracking number"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Typography>

                  <Typography variant="body1">
                    <strong>
                      {t("admin.detail.shippedDate", "Shipped Date")}:
                    </strong>{" "}
                    {order.metadata.shippedDate
                      ? new Date(
                          order.metadata.shippedDate
                        ).toLocaleDateString()
                      : "Not available"}
                  </Typography>

                  {(order.shippingMethod?.trackingUrlFormat ||
                    order.metadata.trackingNumber) && (
                    <Button
                      variant="contained"
                      size="small"
                      component="a"
                      href={
                        order.shippingMethod?.trackingUrlFormat
                          ? order.shippingMethod.trackingUrlFormat.replace(
                              "{trackingNumber}",
                              order.metadata.trackingNumber
                            )
                          : `https://track.yourpackage.com/${order.metadata.trackingNumber}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ alignSelf: "flex-start", mt: 1 }}
                      startIcon={<LocalShippingIcon />}
                    >
                      {t("admin.detail.trackPackage", "Track Package")}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("shared.orderDetails.items")}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("shared.orderDetails.product")}</TableCell>
                    <TableCell>{t("shared.orderDetails.sku")}</TableCell>
                    <TableCell align="right">
                      {t("shared.orderDetails.price")}
                    </TableCell>
                    <TableCell align="center">
                      {t("shared.orderDetails.quantity")}
                    </TableCell>
                    <TableCell align="right">
                      {t("shared.orderDetails.subtotal")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/products/${
                            item.product?.parentId
                              ? item.product.parentId
                              : item.productId
                          }`}
                        >
                          {item.product?.name || tCommon("status.notFound")}
                        </Link>
                      </TableCell>
                      <TableCell>{item.product?.sku || "N/A"}</TableCell>
                      <TableCell align="right">
                        {`${
                          typeof item.price === "number" ||
                          (typeof item.price === "string" &&
                            !isNaN(parseFloat(item.price)))
                            ? parseFloat(item.price).toFixed(2)
                            : "N/A"
                        } CHF`}
                      </TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="right">
                        {`${
                          typeof item.price === "number" ||
                          (typeof item.price === "string" &&
                            !isNaN(parseFloat(item.price)))
                            ? (parseFloat(item.price) * item.quantity).toFixed(
                                2
                              )
                            : "N/A"
                        } CHF`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{dialogContent.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogContent.message}
          </DialogContentText>
          {dialogContent.title ===
            t("admin.detail.dialog.markAsShipped.title") && (
            <>
              <TextField
                autoFocus
                margin="dense"
                id="trackingNumber"
                label={t("admin.detail.dialog.markAsShipped.trackingNumber")}
                type="text"
                fullWidth
                variant="outlined"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                helperText={
                  order.shippingMethod?.trackingUrlFormat
                    ? t(
                        "admin.detail.dialog.markAsShipped.trackingFormatHelp",
                        {
                          format:
                            order.shippingMethod.trackingUrlFormat.replace(
                              "{trackingNumber}",
                              "TRACKING"
                            ),
                        }
                      )
                    : t("admin.detail.dialog.markAsShipped.noTrackingFormat")
                }
                InputProps={{
                  endAdornment: trackingNumber && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="copy tracking number"
                        onClick={() =>
                          copyToClipboard(
                            trackingNumber,
                            t(
                              "admin.detail.trackingCopied",
                              "Tracking number copied"
                            )
                          )
                        }
                        edge="end"
                        size="small"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("admin.detail.dialog.markAsShipped.shippingMethod")}:
                </Typography>
                <Typography variant="body2">
                  {order.shippingMethod?.name ||
                    order.shippingMethodName ||
                    "Standard Shipping"}
                </Typography>
                {order.shippingMethod?.trackingUrlFormat && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1 }} gutterBottom>
                      {t("admin.detail.dialog.markAsShipped.trackingUrlFormat")}
                      :
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {order.shippingMethod.trackingUrlFormat}
                    </Typography>
                  </>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            disabled={!!actionLoading}
          >
            {tCommon("buttons.cancel")}
          </Button>
          <Button
            onClick={() => {
              console.log("Confirm button clicked in dialog");
              console.log(`Current tracking number value: "${trackingNumber}"`);

              // Use the current value directly from state, not from the closure
              const currentTracking = trackingNumber;

              // We'll use this in the executeAction function
              const shipOrder = async () => {
                console.log(
                  `Actually shipping with tracking: "${currentTracking}"`
                );
                return await orderService.markAsShipped(
                  orderId,
                  currentTracking
                );
              };

              dialogContent.onConfirm(shipOrder);
            }}
            color="primary"
            autoFocus
            disabled={!!actionLoading}
          >
            {actionLoading ? (
              <CircularProgress size={24} />
            ) : dialogContent.title ===
              t("admin.detail.dialog.markAsShipped.title") ? (
              t("admin.detail.dialog.markAsShipped.confirmShipment")
            ) : (
              tCommon("buttons.confirm")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminOrderDetailPage;
